import { useEffect, useState } from 'react';
import { AUTH_URL, APP_ID } from '../../../../services/apiConfig';
import { fetchJsonWithAuth } from '../../../../services/authApi';
import { normalizeOption } from '../utils/popupUtils';

function fieldSupportsMasterOptions(field) {
  return ['select', 'multi-select', 'search-select', 'radio'].includes(field?.type)
    && field?.dataSource === 'master'
    && field?.masterName;
}

function unwrapMasterRows(json) {
  const payload = json?.data ?? json;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
}

function isClientSubmissionStatusMaster(field) {
  const masterName = String(field?.masterName ?? '').trim().toLowerCase();
  return masterName === 'clientsubmissions' || masterName === 'clientsubmissionstatus';
}

function normalizeClientSubmissionStatusOption(item) {
  if (item === null || item === undefined) return null;

  // clientSubmissionStatus can be returned by different backend versions as:
  //   { id, name, type, ...transitionFields }
  //   { uniqueId, name, type, ...transitionFields }
  //   { label, value, ...transitionFields }
  // Do not pass this master through the generic normalizer because its
  // label/value aliases have changed over time. Build one stable option shape.
  if (typeof item === 'string') {
    const text = item.trim();
    return text ? { label: text, value: text, _raw: { name: text } } : null;
  }

  const name = String(
    item?.name
      ?? item?.clientSubmissionStatusText
      ?? item?.label
      ?? item?.text
      ?? '',
  ).trim();
  if (!name) return null;

  const id = Number(
    item?.id
      ?? item?.uniqueId
      ?? item?.clientSubmissionStatusId
      ?? item?.value
      ?? 0,
  ) || 0;

  return {
    label: name,
    // The popup form keeps the human-readable status as its value. The submit
    // handler reads the numeric id/type from _raw and sends the full object.
    value: name,
    _raw: {
      ...item,
      ...(id > 0 ? { id, uniqueId: id } : {}),
      name,
      type: String(item?.type ?? item?.clientSubmissionStatusType ?? '').trim(),
      isInitialStatus: item?.isInitialStatus === true,
      isTerminalStatus: item?.isTerminalStatus === true,
      allowedNextStatusIds: Array.isArray(item?.allowedNextStatusIds)
        ? item.allowedNextStatusIds.map(Number).filter((nextId) => Number.isInteger(nextId) && nextId > 0)
        : [],
    },
  };
}

function normalizeMasterOption(item, field) {
  if (isClientSubmissionStatusMaster(field)) {
    return normalizeClientSubmissionStatusOption(item);
  }
  return normalizeOption(item, field);
}

function currentClientSubmissionStatus(record) {
  const status = record?.clientSubmissionStatus;

  const id = Number(
    (status && typeof status === 'object'
      ? (
        status?.clientSubmissionStatusId
        ?? status?.id
        ?? status?.uniqueId
      )
      : undefined)
      ?? record?.clientSubmissionStatusId
      ?? record?.clientStatusId
      ?? record?.['clientSubmissionStatus.clientSubmissionStatusId']
      ?? 0,
  ) || 0;

  const statusObjectText = status && typeof status === 'object'
    ? (
      status?.clientSubmissionStatusText
      ?? status?.name
      ?? status?.text
      ?? status?.label
    )
    : status;

  const text = String(
    statusObjectText
      ?? record?.clientSubmissionStatusText
      ?? record?.clientStatus
      ?? record?.clientStatusText
      ?? record?.['clientSubmissionStatus.clientSubmissionStatusText']
      ?? '',
  ).trim();

  return { id, text };
}

function isUnsubmittedClientStatus(status) {
  const id = Number(status?.id ?? 0) || 0;
  const text = String(status?.text ?? '').trim();

  // A list row may contain only the displayed client status text and omit the
  // numeric status id. Likewise, detail data can sometimes contain the id but
  // not the projected text. Either one is enough to identify an existing
  // client-submission status. Only treat the record as unsubmitted when BOTH
  // are missing, or when it explicitly says "Not Submitted".
  if (text && text.toLowerCase() === 'not submitted') return true;
  return id <= 0 && !text;
}

function optionStatusId(option) {
  return Number(option?._raw?.id ?? option?._raw?.uniqueId ?? 0) || 0;
}

function optionAllowedNextIds(option) {
  const ids = option?._raw?.allowedNextStatusIds;
  return Array.isArray(ids) ? ids.map(Number).filter((id) => Number.isInteger(id) && id > 0) : [];
}


function unwrapDetailRecord(json) {
  const payload = json?.data ?? json;
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] ?? null;
  if (payload?.record && typeof payload.record === 'object') return payload.record;
  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) return payload.data;
  return typeof payload === 'object' ? payload : null;
}

function contextHasCurrentClientStatus(popupContext) {
  const selectedRecords = Array.isArray(popupContext?.selectedRecords)
    ? popupContext.selectedRecords.filter(Boolean)
    : [];

  if (selectedRecords.some((record) => !isUnsubmittedClientStatus(currentClientSubmissionStatus(record)))) {
    return true;
  }

  return !isUnsubmittedClientStatus(currentClientSubmissionStatus(popupContext ?? {}));
}

async function enrichPopupContextWithCurrentSubmissionStatus(popupContext, appId) {
  if (contextHasCurrentClientStatus(popupContext)) return popupContext;

  const selectedId = popupContext?.selectedIds?.[0]
    ?? popupContext?.selectedRecords?.[0]?._id
    ?? popupContext?.selectedRecords?.[0]?.id;
  if (!selectedId) return popupContext;

  const moduleName = String(popupContext?.moduleName ?? 'submissions').trim() || 'submissions';
  const params = new URLSearchParams({
    module: moduleName,
    id: String(selectedId),
  });
  if (appId) params.set('app_id', appId);

  try {
    const detailJson = await fetchJsonWithAuth(
      AUTH_URL,
      `/module-data-detail?${params.toString()}`,
      { headers: appId ? { 'X-App-Id': appId } : undefined },
    );
    const detailRecord = unwrapDetailRecord(detailJson);
    if (!detailRecord) return popupContext;

    const existingRecords = Array.isArray(popupContext?.selectedRecords)
      ? popupContext.selectedRecords
      : [];

    return {
      ...popupContext,
      selectedRecords: existingRecords.length
        ? existingRecords.map((record, index) => (
          index === 0 ? { ...record, ...detailRecord } : record
        ))
        : [detailRecord],
    };
  } catch (error) {
    console.warn(
      '[DynamicPopup] Could not load selected submission detail for client status transition:',
      error,
    );
    return popupContext;
  }
}

function filterClientSubmissionStatusOptions(options, popupContext) {
  if (!Array.isArray(options) || !options.length) return [];

  const hasTransitionConfiguration = options.some((option) => (
    option?._raw?.isInitialStatus === true
    || option?._raw?.isTerminalStatus === true
    || Array.isArray(option?._raw?.allowedNextStatusIds)
  ));
  if (!hasTransitionConfiguration) return options;

  const selectedRecords = Array.isArray(popupContext?.selectedRecords)
    ? popupContext.selectedRecords.filter(Boolean)
    : [];

  const contextStatus = {
    clientSubmissionStatus: popupContext?.clientSubmissionStatus,
    clientSubmissionStatusId: popupContext?.clientSubmissionStatusId,
    clientSubmissionStatusText: popupContext?.clientSubmissionStatusText,
    clientStatus: popupContext?.clientStatus,
    clientStatusId: popupContext?.clientStatusId,
    clientStatusText: popupContext?.clientStatusText,
    'clientSubmissionStatus.clientSubmissionStatusId':
      popupContext?.['clientSubmissionStatus.clientSubmissionStatusId'],
    'clientSubmissionStatus.clientSubmissionStatusText':
      popupContext?.['clientSubmissionStatus.clientSubmissionStatusText'],
  };

  const records = selectedRecords.length ? selectedRecords : [contextStatus];

  return options.filter((candidateOption) => records.every((record) => {
    let currentStatus = currentClientSubmissionStatus(record);

    // Some list configurations only project the visible status text into
    // popupContext rather than keeping the complete nested status object.
    // Use the popup context as a secondary source before considering the
    // submission "not submitted".
    if (isUnsubmittedClientStatus(currentStatus)) {
      const fallbackStatus = currentClientSubmissionStatus(contextStatus);
      if (!isUnsubmittedClientStatus(fallbackStatus)) {
        currentStatus = fallbackStatus;
      }
    }
    if (isUnsubmittedClientStatus(currentStatus)) {
      return candidateOption?._raw?.isInitialStatus === true;
    }

    const currentOption = options.find((option) => {
      const id = optionStatusId(option);
      const text = String(option?.label ?? '').trim();
      return (currentStatus.id > 0 && id === currentStatus.id)
        || (currentStatus.text && text.toLowerCase() === currentStatus.text.toLowerCase());
    });
    if (!currentOption || currentOption?._raw?.isTerminalStatus === true) return false;

    const allowedIds = optionAllowedNextIds(currentOption);
    return allowedIds.includes(optionStatusId(candidateOption));
  }));
}

/**
 * Manages dynamic option fetching and extra-data loading for DynamicPopup.
 *
 * Uses AbortController (latest-request-wins) so that state is never updated
 * from a response that arrived after the popup closed or the deps changed.
 *
 * @param {Object} params
 * @param {boolean} params.open
 * @param {Object|null} params.config
 * @param {Object} params.api - { getOptions?, loadExtraData? }
 * @param {Array} params.fields
 * @param {Object} params.popupContext
 * @param {Object} params.formValues
 * @returns {{ dynamicOptions: Object, setDynamicOptions: Function, extraData: * }}
 */
export function usePopupOptions({ open, config, api, fields, popupContext, formValues }) {
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [extraData, setExtraData] = useState(null);

  // Clear stale data when the popup closes so the next open starts fresh
  useEffect(() => {
    if (!open) {
      setDynamicOptions({});
      setExtraData(null);
    }
  }, [open]);

  // Fetch options that have no form-value dependency (runs once on open)
  useEffect(() => {
    if (!open || !config || !api.getOptions) return;

    const controller = new AbortController();

    fields.forEach((field) => {
      if (!field.fetchOptionsKey || field.fetchOptionsParamKey) return;

      Promise.resolve(api.getOptions(field, undefined, popupContext))
        .then((items) => {
          if (controller.signal.aborted) return;
          setDynamicOptions((prev) => ({ ...prev, [field.name]: items }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Failed to load options for field "${field.name}":`, err);
        });
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, fields, popupContext]);

  // Fetch master-backed dropdown options declared directly on popup fields.
  useEffect(() => {
    if (!open || !config) return;

    const masterFields = fields.filter(fieldSupportsMasterOptions);
    if (!masterFields.length) return;

    const controller = new AbortController();

    masterFields.forEach((field) => {
      const params = new URLSearchParams({
        masterName: field.masterName,
        offset: '0',
        limit: String(field.limit ?? 50),
      });
      const appId = String(APP_ID ?? '').trim();
      if (appId) params.set('app_id', appId);

      fetchJsonWithAuth(AUTH_URL, `/masters/dropdown?${params.toString()}`, {
        headers: appId ? { 'X-App-Id': appId } : undefined,
      })
        .then(async (json) => {
          if (controller.signal.aborted) return;
          const normalizedOptions = unwrapMasterRows(json)
            .map((item) => normalizeMasterOption(item, field))
            .filter(Boolean);

          let optionContext = popupContext;
          if (isClientSubmissionStatusMaster(field)) {
            optionContext = await enrichPopupContextWithCurrentSubmissionStatus(
              popupContext,
              appId,
            );
          }
          if (controller.signal.aborted) return;

          const options = isClientSubmissionStatusMaster(field)
            ? filterClientSubmissionStatusOptions(normalizedOptions, optionContext)
            : normalizedOptions;
          setDynamicOptions((prev) => ({ ...prev, [field.name]: options }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Failed to load master options for field "${field.name}":`, err);
        });
    });

    return () => controller.abort();
  }, [open, config, fields, popupContext]);

  // Fetch options that depend on another form field value (re-runs when that value changes)
  useEffect(() => {
    if (!open || !config || !api.getOptions) return;

    const paramFields = fields.filter((f) => f.fetchOptionsKey && f.fetchOptionsParamKey);
    if (!paramFields.length) return;

    const controller = new AbortController();

    paramFields.forEach((field) => {
      const paramValue =
        formValues[field.fetchOptionsParamKey] ?? popupContext[field.fetchOptionsParamKey];
      const fetchParams = { [field.fetchOptionsParamKey]: paramValue };

      Promise.resolve(api.getOptions(field, fetchParams, popupContext))
        .then((items) => {
          if (controller.signal.aborted) return;
          setDynamicOptions((prev) => ({ ...prev, [field.name]: items }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(
            `[DynamicPopup] Failed to load param-dependent options for "${field.name}":`,
            err,
          );
        });
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, fields, formValues, popupContext]);

  // Load extra data (e.g. existing recruiters for the others-assign popup)
  useEffect(() => {
    if (!open || !config || !api.loadExtraData) return;

    const controller = new AbortController();

    Promise.resolve(api.loadExtraData(popupContext))
      .then((data) => {
        if (controller.signal.aborted) return;
        setExtraData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('[DynamicPopup] Failed to load extra data:', err);
        setExtraData(null);
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api, config, popupContext]);

  return { dynamicOptions, setDynamicOptions, extraData };
}
